import { useState, useEffect, useCallback } from 'react'
import './AlbumArtViewer.css'

const IMAGE_SLOTS = [
  { id: 'front', label: 'Front Cover' },
  { id: 'insideFront', label: 'Inside Front' },
  { id: 'insideBack', label: 'Inside Back' },
  { id: 'back', label: 'Back Cover' },
]

// Transition types between image indices
const TRANSITIONS = {
  '0-1': 'peel',   // Front -> Inside Front: Book opening
  '1-2': 'slide',  // Inside Front -> Inside Back: Slide
  '2-3': 'flip',   // Inside Back -> Back: Page flip
}

// Animation durations in ms
const DURATIONS = {
  peel: 800,
  slide: 500,
  flip: 700,
  open: 400,
  close: 300,
}

const AUTO_ADVANCE_DELAY = 3500
const USER_PAUSE_DURATION = 10000

// Placeholder for missing images
const PLACEHOLDER_ART = 'data:image/svg+xml,' + encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
    <rect fill="#1E1A22" width="100" height="100"/>
    <circle cx="50" cy="50" r="30" fill="none" stroke="#6E2EBF" stroke-width="2"/>
    <circle cx="50" cy="50" r="10" fill="#6E2EBF"/>
  </svg>
`)

function getTransitionType(fromIndex, toIndex) {
  const key = fromIndex < toIndex
    ? `${fromIndex}-${toIndex}`
    : `${toIndex}-${fromIndex}`
  return TRANSITIONS[key] || 'slide'
}

export default function AlbumArtViewer({ album, isOpen, onClose }) {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [nextIndex, setNextIndex] = useState(null)
  const [direction, setDirection] = useState('forward')
  const [isAnimating, setIsAnimating] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [isOpening, setIsOpening] = useState(true)
  const [isPaused, setIsPaused] = useState(false)

  // Get available images (filter out missing ones)
  const availableSlots = IMAGE_SLOTS.filter(
    slot => album?.images?.[slot.id]
  )

  // If no images, use front with placeholder
  const displaySlots = availableSlots.length > 0 ? availableSlots : [IMAGE_SLOTS[0]]

  // Reset state when album changes or viewer opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(0)
      setNextIndex(null)
      setDirection('forward')
      setIsAnimating(false)
      setIsClosing(false)
      setIsOpening(true)
      setIsPaused(false)

      // End opening animation
      const timer = setTimeout(() => {
        setIsOpening(false)
      }, DURATIONS.open)

      return () => clearTimeout(timer)
    }
  }, [isOpen, album?.id])

  // Get current slot info
  const currentSlot = displaySlots[currentIndex] || displaySlots[0]
  const currentImage = album?.images?.[currentSlot?.id] || PLACEHOLDER_ART

  // Navigation function
  const navigateTo = useCallback((newIndex, dir) => {
    if (isAnimating || isClosing || newIndex < 0 || newIndex >= displaySlots.length) return
    if (newIndex === currentIndex) return

    setDirection(dir)
    setNextIndex(newIndex)
    setIsAnimating(true)

    const transitionType = getTransitionType(currentIndex, newIndex)
    const duration = DURATIONS[transitionType]

    setTimeout(() => {
      setCurrentIndex(newIndex)
      setNextIndex(null)
      setIsAnimating(false)
    }, duration)
  }, [currentIndex, displaySlots.length, isAnimating, isClosing])

  const navigateNext = useCallback(() => {
    if (currentIndex < displaySlots.length - 1) {
      navigateTo(currentIndex + 1, 'forward')
    }
  }, [currentIndex, displaySlots.length, navigateTo])

  const navigatePrevious = useCallback(() => {
    if (currentIndex > 0) {
      navigateTo(currentIndex - 1, 'backward')
    }
  }, [currentIndex, navigateTo])

  const handleUserInteraction = useCallback(() => {
    setIsPaused(true)
  }, [])

  // Close with animation
  const handleClose = useCallback(() => {
    setIsClosing(true)
    setTimeout(() => {
      setIsClosing(false)
      setCurrentIndex(0)
      setIsPaused(false)
      onClose()
    }, DURATIONS.close)
  }, [onClose])

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e) => {
      switch (e.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault()
          navigateNext()
          handleUserInteraction()
          break
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault()
          navigatePrevious()
          handleUserInteraction()
          break
        case 'Escape':
          e.preventDefault()
          handleClose()
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, navigateNext, navigatePrevious, handleClose, handleUserInteraction])

  // Auto-advance timer
  useEffect(() => {
    if (!isOpen || isPaused || isAnimating || isClosing || isOpening) return
    if (currentIndex >= displaySlots.length - 1) return // Don't auto-advance past last

    const timer = setTimeout(() => {
      navigateNext()
    }, AUTO_ADVANCE_DELAY)

    return () => clearTimeout(timer)
  }, [isOpen, currentIndex, isPaused, isAnimating, isClosing, isOpening, displaySlots.length, navigateNext])

  // Resume auto-advance after pause
  useEffect(() => {
    if (!isPaused) return

    const resumeTimer = setTimeout(() => {
      setIsPaused(false)
    }, USER_PAUSE_DURATION)

    return () => clearTimeout(resumeTimer)
  }, [isPaused])

  if (!isOpen) return null

  // Determine animation classes
  const transitionType = nextIndex !== null
    ? getTransitionType(currentIndex, nextIndex)
    : null

  const getAnimationClass = () => {
    if (!isAnimating || transitionType === null) return ''
    return `animating-${transitionType}-${direction}-out`
  }

  const getNextAnimationClass = () => {
    if (!isAnimating || transitionType === null) return ''
    return `animating-${transitionType}-${direction}-in`
  }

  const nextSlot = nextIndex !== null ? displaySlots[nextIndex] : null
  const nextImage = nextSlot ? (album?.images?.[nextSlot.id] || PLACEHOLDER_ART) : null

  return (
    <div
      className="album-viewer-overlay"
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Album artwork for ${album?.title}`}
    >
      <div
        className={`album-viewer-container ${isOpening ? 'opening' : ''} ${isClosing ? 'closing' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Current image */}
        <img
          src={currentImage}
          alt={currentSlot?.label}
          className={`album-viewer-image current ${getAnimationClass()}`}
          onError={(e) => { e.target.src = PLACEHOLDER_ART }}
        />

        {/* Next image (shown during transition) */}
        {nextIndex !== null && nextImage && (
          <img
            src={nextImage}
            alt={nextSlot?.label}
            className={`album-viewer-image next ${getNextAnimationClass()}`}
            onError={(e) => { e.target.src = PLACEHOLDER_ART }}
          />
        )}

        {/* Album info */}
        <div className="album-viewer-info">
          <h3>{album?.title}</h3>
          <p>{album?.artist}</p>
        </div>

        {/* Close button */}
        <button
          className="album-viewer-close"
          onClick={handleClose}
          aria-label="Close viewer"
        >
          &times;
        </button>

        {/* Navigation controls */}
        {displaySlots.length > 1 && (
          <div className="album-viewer-controls">
            <button
              className="viewer-nav viewer-nav-prev"
              onClick={() => { navigatePrevious(); handleUserInteraction() }}
              disabled={currentIndex === 0 || isAnimating}
              aria-label="Previous page"
            >
              &#8249;
            </button>
            <button
              className="viewer-nav viewer-nav-next"
              onClick={() => { navigateNext(); handleUserInteraction() }}
              disabled={currentIndex === displaySlots.length - 1 || isAnimating}
              aria-label="Next page"
            >
              &#8250;
            </button>
          </div>
        )}

        {/* Page label */}
        <div className="album-viewer-label">
          {currentSlot?.label}
        </div>

        {/* Page indicators */}
        {displaySlots.length > 1 && (
          <div className="album-viewer-indicators">
            {displaySlots.map((slot, index) => (
              <button
                key={slot.id}
                className={`indicator ${index === currentIndex ? 'active' : ''}`}
                onClick={() => {
                  const dir = index > currentIndex ? 'forward' : 'backward'
                  navigateTo(index, dir)
                  handleUserInteraction()
                }}
                disabled={isAnimating}
                aria-label={slot.label}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
